import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Localizer } from 'services/Localizer';
import { AddToCartResponse } from 'services/UserData.Commerce';
import Modal from 'shared/components/Modal';
import Spinner from 'shared/components/Spinner';
import CheckCircleSVG from 'shared/components/svg/CheckCircleSVG';
import InlineAlert from 'shared/edit/InlineAlert';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { assertNotNullish } from 'util/type-assertions';
import { OrderMessageAction } from '../shopping-cart/OrderMessages';
import useShoppingCart, { UseShoppingCart } from '../shopping-cart/useShoppingCart';
import AddedToCartAlert from '../store/AddedToCartAlert';
import { useProductDetailsContext } from './ProductDetailsContext';
import { CheckableLine, default as ProductPricingLines } from './ProductPricingLines';
import {
	AddToCartExceptionCode,
	Availability,
	ProductAddOnDto,
	ProductPricingLinesDetails,
	ProductType,
	Summary,
} from './types';
import { UseProductDetails } from './useProductDetails';
import MinusSVG from 'shared/components/svg/MinusSVG';
import PlusSVG from 'shared/components/svg/PlusSVG';
import ShoppingCartSVG from 'shared/components/svg/ShoppingCartSVG';
import ProductAvailabilityHCL from './ProductAvailabilityHCL';

/** The component props for the ProductAddToCart component. */
interface ProductAddToCartProps {
	id: string;
	summary?: Summary;
	availability?: Availability | null;
	pricingLines?: ProductPricingLinesDetails;
	onPricingLineChange: (pricingLineId: string) => void;

	quantity: number | null;
	setQuantity: (qty: number | null) => void;

	setSelectedProductPricingLineId: (id: string | null) => void;

	addOns?: ProductAddOnDto[];
	addOnsHandleSubmitWithError?: (error: AxiosError) => void;
}

/**
 * Represents the component on a product details page that manages pricing line selections,
 * quantities, and of coursing, adding a product to the user's shopping cart.
 */
export default function ProductAddToCart(props: ProductAddToCartProps) {
	const UseProductDetailsResult: UseProductDetails = useProductDetailsContext();

	const {
		state: { selectedPricingLine, productDetails },
		actor: { setSelectedPricingLine, isPricingLineUnavailable, isPurchaseable },
	} = UseProductDetailsResult;

	const [modalOpen, setModalOpen] = useState(true);
	const [addingToCart, setAddingToCart] = useState(false);
	const [addToCartResponse, setAddToCartResponse] = useState<AddToCartResponse | null>(null);
	const [errorMessages, setErrorMessages] = useState<Array<string>>([]);
	const [isViewCartActive, setIsViewCartActive] = useState<boolean>(false);
	const [numberOfItems, setNumberOfItems] = useState<number>(props.quantity || 1);

	const UseShoppingCartResult: UseShoppingCart = useShoppingCart();

	const {
		state: { error, errorMessage: cartErrorMessage, errorType },
		actor: { addToCart },
	} = UseShoppingCartResult;

	const allSelectionsSelected = props.summary && props.summary.type === ProductType.Simple;
	const errorTitle =
		errorType === AddToCartExceptionCode.cartFull
			? Localizer('CartFull')
			: errorType === AddToCartExceptionCode.productOutOfStock
			? Localizer('OutOfStock')
			: errorType === AddToCartExceptionCode.productUnavailable
			? Localizer('ProductUnavailable')
			: Localizer('ErrorMessageGenericTitle');

	useEffect(() => {
		if (allSelectionsSelected) {
			setErrorMessages([]);
			setAddingToCart(false);
		}

		if (cartErrorMessage) {
			setErrorMessages([cartErrorMessage]);
		}
	}, [props.summary, allSelectionsSelected, cartErrorMessage]);

	//#region Render method "subcomponent" helpers

	//#region AddToCartButton Subcomponent
	// The "Add to Cart" button. When pressed, the selected product will be added to the cart.
	// Manages its own visibility and "clickability", disabling itself if the selected option is
	// erroneous such as if it's not for sale.

	/** Event fired when the "add to cart" button is clicked. */
	const handleAddToCart = async () => {
		setErrorMessages([]);
		if (!allSelectionsSelected || !selectedPricingLine.checked || isPricingLineUnavailable()) {
			setErrorMessages([Localizer('AddToCartSelectionErrorMessage')]);
			setIsViewCartActive(false);
			return;
		}

		setAddToCartResponse(null);

		try {
			console.debug('Adding to cart:', props.id, props.quantity);
			setAddingToCart(true);

			const response = await addToCart({
				itemId: props.id,
				quantity: props.quantity,
				productPricingLineId: selectedPricingLine.pricingLine?.id ?? '',
				addOns: props.addOns,
				validateAddOns: true,
			});

			if (!response) {
				setAddToCartResponse(null);
				return;
			}

			setAddToCartResponse(response);

			const orderErrorMessages = response.orderMessages?.filter(
				(om) =>
					om.messageAction === OrderMessageAction.Error ||
					om.messageAction === OrderMessageAction.RedirectToCart,
			);

			if (orderErrorMessages && orderErrorMessages.length > 0) {
				setErrorMessages(orderErrorMessages.map((orderMessages) => orderMessages.message));
				setModalOpen(false);
				setIsViewCartActive(true);
			} else {
				setModalOpen(true);
			}

			dispatchRocEvent('roc-cart-summary-updated', { cartSummary: response.cartSummary });
		} catch (error) {
			setAddToCartResponse(null);

			const axiosError: AxiosError = error;
			if (axiosError && axiosError.response && axiosError.response.status === 400 && axiosError.response.data) {
				//notify the add ons form with the validation errors
				if (props.addOnsHandleSubmitWithError) {
					props.addOnsHandleSubmitWithError(axiosError);
				}
			}
		}

		setAddingToCart(false);
	};
	//#endregion

	//#region PricingLineControls Subcomponent
	// Represents the pricing line and quantity controls, dictating the number or quantity of products added to the
	// end user's cart and which pricing line is selected.

	/** Callback when a pricing line's quantity is changed in PricingLineControls */
	const updateQuantity = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const newQuantity = parseInt(e.currentTarget.value);
		// #region HCL
		setNumberOfItems(newQuantity);
		// #endregion

		if (isNaN(newQuantity)) {
			props.setQuantity(null);
			return;
		}

		if (newQuantity > 0) {
			props.setQuantity(newQuantity);
		}
	};

	/** Callback when a pricing line is changed in PricingLineControls */
	const handlePricingLineChange = (line: CheckableLine) => {
		if (selectedPricingLine.pricingLine?.id === line.pricingLine?.id) {
			return;
		}

		setSelectedPricingLine(line);
		props.setSelectedProductPricingLineId(line.pricingLine?.id ?? '');

		// trigger unit of measure change on the parent component only in case it is not an initialization process
		// because this will reload the data like pricing, availability, etc.
		if (!line.isInitialization) {
			assertNotNullish(line.pricingLine);
			props.onPricingLineChange(line.pricingLine.id);
		}
		// #region HCL: Reset quantity to 1 when pricing line is changed
		setNumberOfItems(1);
		// #endregion
	};

	function handleUpdateQuantity(qty: number) {
		setNumberOfItems(qty);
		if (qty > 0) {
			props.setQuantity(qty);
		}
	}

	//#endregion

	//#region ErrorAlerts Subcomponent
	/**
	 * Represents the conditional inline error alerts subcomponent that may display if an unexpected error occured during operation.
	 * This component manages its own visibility.
	 */
	//#endregion
	//#endregion

	return (
		<>
			{/* PricingLineControls Subcomponent */}
			<div className="roc-pdp-main-info__form-group">
				{props.pricingLines != null ? (
					<>
						{isPurchaseable(productDetails.availability, selectedPricingLine.pricingLine) ? (
							<>
								<label htmlFor="quantity" className="roc-pdp-main-info__label">
									<p className="roc-pdp-main-info__label-text">{Localizer('Qty')}</p>
									<div className="roc-pdp__qty-input-container">
										<button
											className="roc-btn"
											type="button"
											onClick={() => handleUpdateQuantity(numberOfItems - 1)}
											disabled={numberOfItems === 1}
										>
											<MinusSVG />
										</button>
										<input
											name="quantity"
											type="number"
											autoComplete="off"
											id="quantity"
											tabIndex={-1}
											className="roc-form-input__input roc-pdp__qty-input"
											value={numberOfItems}
											onChange={(e) => updateQuantity(e)}
											data-testid="quantity-textbox"
										/>
										<button
											className="roc-btn"
											type="button"
											onClick={() => handleUpdateQuantity(numberOfItems + 1)}
										>
											<PlusSVG />
										</button>
									</div>
								</label>
							</>
						) : null}
						<ProductPricingLines
							details={props.pricingLines}
							onPricingLineChange={handlePricingLineChange}
						/>
					</>
				) : null}
			</div>

			{/* AddToCart Subcomponent */}
			<div className="roc-add-to-cart-btn-wrap">
				{/* #region HCL - add component to display inventory status */}
				<ProductAvailabilityHCL availability={productDetails.availability} />
				{/* #endregion */}
				<button
					onClick={() => handleAddToCart()}
					type="button"
					className="roc-btn roc-btn--block roc-btn--big"
					disabled={
						addingToCart ||
						props.quantity == null ||
						!isPurchaseable(productDetails.availability, selectedPricingLine.pricingLine)
					}
					data-testid="add-to-cart-button"
				>
					{Localizer('AddToCart')}
					<ShoppingCartSVG className="roc-utlity-nav__cart-icon" />
					{addingToCart && <Spinner />}
				</button>
				<button className="roc-icon-print" onClick={() => window.print()}>
					<svg className="roc-icon" focusable="false">
						<use xlinkHref="#icon-printer"></use>
					</svg>
				</button>
			</div>

			{/* AddToCartModal Subcomponent */}
			{addToCartResponse !== null ? (
				<Modal
					isOpen={modalOpen}
					renderTitle={() => Localizer('AddedToCart')}
					closeModal={() => setModalOpen(false)}
					titleIcon={<CheckCircleSVG />}
				>
					<AddedToCartAlert response={addToCartResponse} />
				</Modal>
			) : null}

			{error != null || errorMessages.length > 0 ? (
				<InlineAlert
					theme="danger"
					isTitleOnOwnRow
					title={errorTitle}
					message={
						<>
							{errorMessages && errorMessages.length > 0 ? (
								errorMessages.map((orderMessage, index) => (
									<div key={index}>
										<span dangerouslySetInnerHTML={{ __html: orderMessage }} />
										<br />
									</div>
								))
							) : (
								<span dangerouslySetInnerHTML={{ __html: Localizer('ErrorWhenAddingToCart') }} />
							)}
							<br /> <br />
							{isViewCartActive ? (
								<a
									href="/cart"
									className="roc-btn roc-btn-danger"
									data-testid="add-to-cart-error-view-cart-link"
								>
									{Localizer('ViewCart')}
								</a>
							) : null}
						</>
					}
					error={error}
				/>
			) : null}
		</>
	);
}
